import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header, Footer, ABC_TRUSTPILOT } from '../Common';
import HeroBannerV2 from '@ajat/design-system/ContentBlocks/HeroBannerV2';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import USP from '@ajat/design-system/ContentBlocks/USP';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useMenuItemListing from '../../../../hooks/useMenuItemListing';
import useMenuItemNavigation from '../../../../hooks/useMenuItemNavigation';
import useMenuItemVisibility from '../../../../hooks/useMenuItemVisibility';
import { mapMenuItemListingCards } from '../../../../hooks/widgetMappers';
// The hero wears the school shop's Skolmerch banner photos (desktop + mobile), so
// landing and shop read as one place — change them in schoolShopDisplay.js only.
import { SKOLMERCH_BANNER } from '../SchoolShop/schoolShopDisplay';
import '../../Common/Landing/Landing.css';

/* ══════════════════════════════════════════════════════════════════════════
 * ABC-SE Landing Page
 * Responsive: mobile-first, desktop ≥ 1024px
 *
 * Shop CTAs and product grids follow the SAME visibility rule as the header/
 * footer menu items (useMenuItemVisibility): with Open Shop disabled and no
 * stored school (the "custom URL shops only" launch mode) they hide instead
 * of dead-ending on the closed-shop page. The page then reads as a brochure
 * (hero, USP, story) with working static links.
 * ══════════════════════════════════════════════════════════════════════════ */
const Landing = () => {
  const navigate = useNavigate();

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'ABC');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  /* ── Menu-item default collections (top block = menu_1, bottom = menu_2) ── */
  const { menuItems, showSchoolSelector, openShopEnabled, landingProductsLimit } = useBrandSettings('abcse');
  // Menu-item click resolver (school shop / popup / fallback / closed page)
  const onMenuItemClick = useMenuItemNavigation({ brandSlug: 'abcse', feSlug: 'abc-se' });
  const { isVisible } = useMenuItemVisibility('abcse');
  const menu1Visible = isVisible('menu_1');
  const menu2Visible = isVisible('menu_2');
  const menu1Slug = (menuItems || []).find((m) => m.key === 'menu_1')?.slug;
  const menu2Slug = (menuItems || []).find((m) => m.key === 'menu_2')?.slug;
  // Only fetch listings when the block can actually show.
  const menu1Listing = useMenuItemListing('abcse', menu1Visible ? menu1Slug : null);
  const menu2Listing = useMenuItemListing('abcse', menu2Visible ? menu2Slug : null);
  // Each front-page block shows at most the brand's configured number of cards (Brands form).
  const menu1Cards = (mapMenuItemListingCards(menu1Listing, { feSlug: 'abc-se', navigate, showSchoolSelector, openShopEnabled, menuKey: 'menu_1', onMenuItemClick, packageDescription: 'Paketlösning' }) ?? []).slice(0, landingProductsLimit);
  const menu2Cards = (mapMenuItemListingCards(menu2Listing, { feSlug: 'abc-se', navigate, showSchoolSelector, openShopEnabled, menuKey: 'menu_2', onMenuItemClick, packageDescription: 'Paketlösning' }) ?? []).slice(0, landingProductsLimit);

  return (
    <div className="landing">
      {/* ── Header ── */}
      <Header />

      {/* ── Hero Banner V2 (full photo; CTA only when the shop is actually reachable) ── */}
      <HeroBannerV2
        subtitle="SVERIGES SKARPASTE PRISER MED PRISGARANTI"
        title="Kläder för ökad samhörighet"
        copy="Med våra 20-års erfarenhet kan vi se till att skapa merch i hög kvalité oavsett om du är en skola, elevkår eller förening!"
        ctaLabel={menu1Visible ? 'Köp Skolmerch' : ''}
        ctaOnClick={() => onMenuItemClick('menu_1')}
        image={SKOLMERCH_BANNER.backgroundImage}
        imageMobile={SKOLMERCH_BANNER.backgroundImageMobile}
        imageAlt="Skolmerch"
      />

      {/* ── USP / Trustpilot Bar ── */}
      <USP
        width="layout"
        items={[
          { icon: 'fast-delivery', label: 'Snabb leverans' },
          { icon: 'size-guarantee', label: '100% storleksgaranti' },
          { icon: 'payment', label: 'Enkel individuell betalning' },
        ]}
        trustpilot={ABC_TRUSTPILOT}
      />

      {/* ── Products Widget 1: Collection (menu_1's collection; hidden when
             the shop isn't reachable — no dead cards, no Danish demo data) ── */}
      {menu1Visible && menu1Cards.length > 0 && (
        <ProductsGrid
          moreLabel="fler"
          background="var(--color-gray-025)"
          title="Kollektion"
          subtitle="Alla plagg i vår kollektion är noggrant testade för att uppfylla våra höga kvalitetskrav, tvätt efter tvätt."
          ctaLabel="Se mer"
          rowGap='medium-s'
          columnGap='medium-s'
          ctaIcon={<ArrowRight size={20} />}
          ctaOnClick={() => onMenuItemClick('menu_1')}
          items={menu1Cards}
          centerIncomplete
        />
      )}

      {/* ── Fullwidth Content Widget 1 (merch print specs — shows/hides together
             with the Skolmerch grid above) ── */}
      {menu1Visible && menu1Cards.length > 0 && (
        <Fullwidth
          showImage={false}
          subtitle="50.000+ ELEVER VÄLJER OSS VARJE ÅR"
          mainCopyVariant="h2"
          mainCopy="Vi erbjuder snabb leverans och attraktiva paketlösningar, och du kan beställa säkert med över 100 års erfarenhet."
          tags={['A4 ryggtryck', '10 × 10 cm bröstlogo', '2 tryckfärger per klass']}
          showHyperlink={false}
        />
      )}

      {/* ── Products Widget 2: Overalls & More (menu_2) ── */}
      {menu2Visible && menu2Cards.length > 0 && (
        <ProductsGrid
          moreLabel="fler"
          background="var(--color-gray-0)"
          ctaLabel="Se mer"
          ctaOnClick={() => onMenuItemClick('menu_2')}
          title="Overalls & More"
          rowGap='medium-s'
          columnGap='medium-s'
          subtitle="Spara upp till 20% på paketlösningar"
          items={menu2Cards}
          centerIncomplete
        />
      )}

      {/* ── Fullwidth Content Widget 2 (with image) ── */}
      <Fullwidth
        subtitle="The ABC Story"
        mainCopyVariant="h2"
        mainCopy="Det här är historien om hur marknaden för studentmössor, skol- & studentkläder och andra studenttillbehör förändrades av tre unga killar."
        additionalCopy="Det handlar även om sättet svenskarna firar sin skolexamen i dag. Det började när Alexander, Babak och Tomas tog studenten tillsammans i Täby utanför Stockholm år 2000. Kul med traditioner tyckte de, men det skulle gå att göra mer, mycket mer."
        image={require('../../../../assets/abc-se/landing/about.jpg')}
        showTags={false}
        showHyperlink={false}
      />

      {/* ── Footer ── */}
      <Footer />
    </div>
  );
};

export default Landing;
